import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatApiError } from "@/lib/AuthContext";
import { Card, Btn, Pill, Modal, Empty, Notice, PageHeader, inputCls } from "@/dashboard/ui";
import PhoneInput from "@/components/PhoneInput";
import {
  PhoneIncoming, PhoneForwarded, PhoneOff, Clock, User, CheckCircle2, XCircle,
  PlayCircle, CalendarClock, FileText, ShieldCheck,
} from "lucide-react";

const DAYS = [["mon", "Mon"], ["tue", "Tue"], ["wed", "Wed"], ["thu", "Thu"], ["fri", "Fri"], ["sat", "Sat"], ["sun", "Sun"]];
const MODES = [
  ["all", "Answer every call", "The AI receptionist answers all incoming calls, day and night."],
  ["out_of_hours", "Out-of-hours only", "Answers outside the opening hours in your Business Profile."],
  ["missed_call", "Missed-call fallback", "Handles calls your team doesn't answer — needs compatible call routing when telephony is connected."],
];
const SCHEDULES = [
  ["24_7", "24/7", "Answers around the clock, every day."],
  ["business_hours", "Business hours", "Only during the opening hours in your Business Profile."],
  ["out_of_hours", "Out of hours", "Only outside your opening hours."],
  ["custom", "Custom schedule", "Your own per-day windows — overnight windows like 18:00 → 08:00 are supported."],
];
const SCENARIOS = [
  ["question", "Business question"], ["appointment", "Appointment enquiry"],
  ["transfer", "Human transfer request"], ["missed", "Missed call"],
];
const OUTCOME_TONE = {
  "Appointment booked": "green", "Appointment requested": "green", "Question answered": "green",
  "Lead captured": "green", "Human transfer requested": "amber", "Follow-up required": "amber",
  "Missed / failed": "red",
};

function CheckRow({ item }) {
  return (
    <div className="flex items-start gap-2 py-1.5" data-testid={`health-${item.check.toLowerCase().replace(/\s+/g, "-")}`}>
      {item.ok ? <CheckCircle2 size={15} className="mt-0.5 shrink-0 text-emerald-600" /> : <XCircle size={15} className="mt-0.5 shrink-0 text-red-500" />}
      <div>
        <p className="text-xs font-semibold text-neutral-800">{item.check}</p>
        <p className="text-[11px] text-neutral-500">{item.detail}</p>
      </div>
    </div>
  );
}

function StatusCard({ status }) {
  const gates = [
    ["Configuration ready", status.configuration_ready],
    ["Simulation available", status.simulation_available],
    ["Telephony connected", false],
    ["Live inbound answering", false],
  ];
  return (
    <Card className="p-5" data-testid="inbound-status-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-red-50"><PhoneOff size={18} className="text-red-500" /></span>
          <div>
            <p className="text-sm font-bold text-[#111111]">Telephony not connected</p>
            <p className="text-xs text-neutral-500" data-testid="inbound-provider-message">{status.provider.message}</p>
          </div>
        </div>
        <Pill tone={status.answering_now ? "green" : "grey"}>
          {status.answering_now
            ? "Schedule: within configured answering hours (simulation only)"
            : "Schedule: outside configured answering hours"}
        </Pill>
      </div>
      <div className="mt-4 grid grid-cols-2 gap-2 md:grid-cols-4">
        {gates.map(([label, ok]) => (
          <div key={label} className={`rounded-xl border px-3 py-2 text-center text-[11px] font-semibold ${ok ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-neutral-200 bg-neutral-50 text-neutral-400"}`}
            data-testid={`gate-${label.toLowerCase().replace(/\s+/g, "-")}`}>
            {label}
          </div>
        ))}
      </div>
    </Card>
  );
}

export default function IncomingCalls() {
  const [status, setStatus] = useState(null);
  const [agents, setAgents] = useState([]);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [calls, setCalls] = useState(null);
  const [outcomes, setOutcomes] = useState([]);
  const [filters, setFilters] = useState({ outcome: "", agent_id: "", date_from: "", date_to: "" });
  const [detail, setDetail] = useState(null);        // loaded call document
  const [detailState, setDetailState] = useState("idle"); // idle | loading | ready | error
  const [sim, setSim] = useState({ caller_name: "Test Caller", caller_number: "+44 7700 900123", scenario: "question", message: "" });
  const [simBusy, setSimBusy] = useState(false);
  const [simResult, setSimResult] = useState(null);

  const loadStatus = async () => {
    try {
      const { data } = await api.get("/inbound/status");
      setStatus(data);
      setForm(data.settings);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const loadCalls = useCallback(async (f = filters) => {
    try {
      const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
      const { data } = await api.get("/inbound/calls", { params });
      setCalls(data.calls);
      setOutcomes(data.outcomes);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [filters]);

  useEffect(() => {
    loadStatus();
    loadCalls();
    api.get("/voice-agents").then((r) => setAgents(r.data || [])).catch(() => {});
    // mount-only initial load; loadCalls is intentionally excluded (see useCallback note)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/inbound/settings", form);
      setForm(data.settings);
      toast.success("Receptionist configuration saved");
      loadStatus();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const runSim = async () => {
    setSimBusy(true);
    setSimResult(null);
    try {
      const { data } = await api.post("/inbound/simulate", sim);
      setSimResult(data);
      toast.success("Simulated call completed — recorded in history");
      loadCalls();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSimBusy(false); }
  };

  const openDetail = async (id) => {
    setDetail(null);
    setDetailState("loading");            // modal opens instantly with a loading state
    try {
      const { data } = await api.get(`/inbound/calls/${id}`);
      setDetail(data);
      setDetailState("ready");
    } catch (e) {
      setDetailState("error");
      toast.error(formatApiError(e));
    }
  };

  const closeDetail = () => { setDetailState("idle"); setDetail(null); };

  // Escape key closes the detail modal (keyboard accessibility)
  useEffect(() => {
    if (detailState === "idle") return undefined;
    const onKey = (e) => { if (e.key === "Escape") closeDetail(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [detailState]);

  const customSched = useMemo(() => form?.custom_schedule || {}, [form]);

  if (!status || !form) return <div className="py-16 text-center text-sm text-neutral-400">Loading…</div>;

  return (
    <div className="space-y-5" data-testid="incoming-calls-page">
      <PageHeader title="Incoming Calls" sub="Your 24/7 AI receptionist — configure it, simulate calls, and review outcomes. Live telephone answering switches on only after a telephony provider is connected." />

      <Card className="p-5" data-testid="receptionist-enable-card">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-sm font-bold text-[#111111]">AI Receptionist</p>
            <p className="mt-0.5 text-xs text-neutral-500" data-testid="receptionist-enable-state">
              {form.enabled
                ? "Enabled — the receptionist answers during your configured schedule (simulation until a provider connects)."
                : "Disabled — no calls are handled by the receptionist."}
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={!!form.enabled}
            aria-label="Enable AI receptionist"
            data-testid="receptionist-enable-toggle"
            onClick={() => set("enabled", !form.enabled)}
            className={`relative h-7 w-12 shrink-0 rounded-full transition-colors ${form.enabled ? "bg-[#111111]" : "bg-neutral-300"}`}
          >
            <span className="absolute top-1 h-5 w-5 rounded-full bg-white transition-all" style={{ left: form.enabled ? 24 : 4 }} />
          </button>
        </div>
      </Card>

      <StatusCard status={status} />

      {/* Phone number */}
      <Card className="p-5" data-testid="phone-number-card">
        <p className="text-xs font-bold uppercase tracking-wider text-neutral-500">Business phone number</p>
        <div className="mt-3 rounded-xl border border-dashed border-neutral-300 bg-neutral-50 p-4">
          <p className="text-sm font-semibold text-neutral-700">No number connected</p>
          <p className="mt-1 text-xs text-neutral-500">
            {status.provider.message} When a provider such as Twilio is approved and connected, your business number, verification status and routing mode will appear here.
          </p>
          <ol className="mt-3 space-y-1 text-[11px] text-neutral-500">
            {status.setup_steps.map((s) => <li key={s}>{s}</li>)}
          </ol>
        </div>
        <div className="mt-3 divide-y divide-neutral-100">
          {status.health.map((h) => <CheckRow key={h.check} item={h} />)}
        </div>
      </Card>

      {/* Telephony integration (read-only — SK-M15A) */}
      <Card className="p-5" data-testid="telephony-integration-card">
        <p className="text-xs font-bold uppercase tracking-wider text-neutral-500">Telephony integration</p>
        <dl className="mt-3 grid grid-cols-1 gap-x-6 gap-y-2 text-xs sm:grid-cols-2" data-testid="telephony-readout">
          {[
            ["Provider", status.telephony?.provider || "Not connected"],
            ["Integration type", status.telephony?.integration_type || "Not configured"],
            ["Phone number", status.telephony?.phone_number || "Not assigned"],
            ["Verification", status.telephony?.verification || "Not verified"],
            ["Live inbound calling", status.telephony?.live_inbound_calling || "Disabled"],
          ].map(([k, v]) => (
            <div key={k} className="flex items-center justify-between border-b border-neutral-100 py-1.5">
              <dt className="text-neutral-500">{k}</dt>
              <dd className="font-semibold text-neutral-800" data-testid={`telephony-${k.toLowerCase().replace(/\s+/g, "-")}`}>{v}</dd>
            </div>
          ))}
        </dl>
        <p className="mt-3 text-[11px] leading-relaxed text-neutral-400">
          The platform's provider-neutral telephony layer is prepared for programmable voice APIs, SIP trunks (via a future media gateway) and custom providers. Connecting a provider, verifying a number and enabling live answering are separate, owner-approved steps — nothing here can place or receive a real call.
        </p>
      </Card>

      {/* Setup */}
      <Card className="p-5" data-testid="receptionist-setup-card">
        <p className="text-xs font-bold uppercase tracking-wider text-neutral-500">AI receptionist setup</p>
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <label className="block">
            <span className="mb-1 block text-xs font-semibold text-neutral-600">Assigned AI voice agent</span>
            <select className={inputCls} value={form.agent_id || ""} data-testid="inbound-agent-select"
              onChange={(e) => set("agent_id", e.target.value)}>
              <option value="">— Choose one of your agents —</option>
              {agents.map((a) => <option key={a.id} value={a.id}>{a.agent_name}</option>)}
            </select>
          </label>
          <div>
            <span className="mb-1 block text-xs font-semibold text-neutral-600">Greeting preview</span>
            <p className="rounded-xl bg-neutral-50 px-3.5 py-2 text-xs italic text-neutral-600" data-testid="greeting-preview">“{status.greeting_preview}”</p>
            <p className="mt-1 text-[11px] text-neutral-400">Built from your Business Profile. The agent's own customised instructions are never overwritten without your approval.</p>
          </div>
        </div>

        <p className="mb-2 mt-5 text-xs font-semibold text-neutral-600">Answering mode</p>
        <div className="grid gap-2 md:grid-cols-3" data-testid="answering-modes">
          {MODES.map(([id, label, desc]) => (
            <button key={id} onClick={() => set("answering_mode", id)} data-testid={`mode-${id}`}
              className={`rounded-xl border p-3 text-left transition-all ${form.answering_mode === id ? "border-[#111111] bg-neutral-50 shadow-sm" : "border-neutral-200 hover:border-neutral-300"}`}>
              <p className="text-xs font-bold text-[#111111]">{label}</p>
              <p className="mt-0.5 text-[11px] leading-snug text-neutral-500">{desc}</p>
            </button>
          ))}
        </div>
        <Notice testid="modes-telephony-note"
          title="These modes describe how the receptionist will behave"
          body="Real answering in any mode requires a connected telephony provider with compatible call routing. Until then, use the simulator below to test behaviour." />

        <p className="mb-2 mt-5 text-xs font-semibold text-neutral-600">Answering schedule <span className="font-normal text-neutral-400">(uses your Business Profile timezone: {status.timezone}; independent of outbound calling hours)</span></p>
        <div className="grid gap-2 md:grid-cols-4" data-testid="schedule-modes">
          {SCHEDULES.map(([id, label, desc]) => (
            <button key={id} onClick={() => set("schedule_mode", id)} data-testid={`schedule-${id}`}
              className={`rounded-xl border p-3 text-left transition-all ${form.schedule_mode === id ? "border-[#111111] bg-neutral-50 shadow-sm" : "border-neutral-200 hover:border-neutral-300"}`}>
              <p className="text-xs font-bold text-[#111111]">{label}</p>
              <p className="mt-0.5 text-[11px] leading-snug text-neutral-500">{desc}</p>
            </button>
          ))}
        </div>

        {form.schedule_mode === "custom" && (
          <div className="mt-3 rounded-xl border border-neutral-200 p-3" data-testid="custom-schedule-editor">
            <p className="mb-2 text-[11px] text-neutral-500">
              Times use your Business Profile timezone ({status.timezone}). An overnight window like 18:00 → 08:00 answers from the evening through to the next morning. Switching a day off keeps its saved hours.
            </p>
            {DAYS.map(([d, label]) => {
              const w = (customSched[d] || [])[0] || null;
              const savedOff = ((form.custom_schedule_saved || {})[d] || [])[0] || null;
              const toggle = (on) => {
                if (on) {
                  const restore = savedOff || { start: "18:00", end: "08:00" };
                  set("custom_schedule", { ...customSched, [d]: [restore] });
                } else {
                  // preserve the hours so turning the day back On restores them
                  set("custom_schedule_saved", { ...(form.custom_schedule_saved || {}), ...(w ? { [d]: [w] } : {}) });
                  set("custom_schedule", { ...customSched, [d]: [] });
                }
              };
              return (
                <div key={d} className="flex flex-wrap items-center gap-2 py-1">
                  <button
                    type="button"
                    role="switch"
                    aria-checked={!!w}
                    data-testid={`sched-${d}-toggle`}
                    onClick={() => toggle(!w)}
                    className={`relative h-5 w-9 rounded-full transition-colors ${w ? "bg-[#111111]" : "bg-neutral-300"}`}
                  >
                    <span className="absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all" style={{ left: w ? 18 : 2 }} />
                  </button>
                  <span className={`w-9 text-xs font-semibold ${w ? "text-neutral-700" : "text-neutral-400"}`}>{label}</span>
                  {w ? (
                    <>
                      <input type="time" className={inputCls + " w-28"} value={w.start} data-testid={`sched-${d}-start`}
                        onChange={(e) => set("custom_schedule", { ...customSched, [d]: [{ ...w, start: e.target.value }] })} />
                      <span className="text-xs text-neutral-400">→</span>
                      <input type="time" className={inputCls + " w-28"} value={w.end} data-testid={`sched-${d}-end`}
                        onChange={(e) => set("custom_schedule", { ...customSched, [d]: [{ ...w, end: e.target.value }] })} />
                      {w.start > w.end && <span className="text-[10px] text-neutral-400">overnight — ends next morning</span>}
                    </>
                  ) : (
                    <span className="text-[11px] text-neutral-400" data-testid={`sched-${d}-off-label`}>
                      Off{savedOff ? ` — saved ${savedOff.start}–${savedOff.end}, switch on to restore` : ""}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <p className="mb-2 mt-5 text-xs font-semibold text-neutral-600">Human transfer & escalation</p>
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <PhoneInput
              testid="transfer-phone"
              label="Transfer phone number"
              value={form.transfer_phone || ""}
              onChange={(v) => set("transfer_phone", v)}
            />
            <span className="mt-1 block text-[11px] text-neutral-400">Transfers only work once telephony is connected — simulations record the request without dialling.</span>
          </div>
          <div className="space-y-2 pt-5">
            {[["transfer_during_hours", "Offer transfer during business hours"], ["transfer_on_request", "Transfer when the caller asks for a person"], ["transfer_on_unresolved", "Transfer when the AI can't resolve the enquiry"]].map(([k, label]) => (
              <label key={k} className="flex items-center gap-2 text-xs text-neutral-700">
                <input type="checkbox" checked={!!form[k]} data-testid={`transfer-${k}`} onChange={(e) => set(k, e.target.checked)} className="h-3.5 w-3.5 rounded" />
                {label}
              </label>
            ))}
          </div>
        </div>
        <div className="mt-4 flex justify-end">
          <Btn data-testid="inbound-save" disabled={saving} onClick={save}>{saving ? "Saving…" : "Save configuration"}</Btn>
        </div>
      </Card>

      {/* Simulation */}
      <Card className="p-5" data-testid="simulate-card">
        <div className="flex items-center gap-2">
          <PlayCircle size={16} className="text-neutral-700" />
          <p className="text-xs font-bold uppercase tracking-wider text-neutral-500">Simulate incoming call</p>
          <Pill tone="amber">SIMULATED — no real call</Pill>
        </div>
        <p className="mt-2 text-[11px] text-neutral-500">
          Test your configuration with a deterministic mock conversation built from your Business Profile — no AI or telephony APIs are used.
          Use a fictional number only: the UK drama range +44 7700 900xxx, or any number containing 555. A simulated call may create a clearly-marked test lead in your CRM.
        </p>
        <div className="mt-3 grid gap-3 md:grid-cols-4">
          <input className={inputCls} placeholder="Caller name" value={sim.caller_name} data-testid="sim-caller-name"
            onChange={(e) => setSim((s) => ({ ...s, caller_name: e.target.value }))} />
          <input className={inputCls} placeholder="+44 7700 900123" value={sim.caller_number} data-testid="sim-caller-number"
            onChange={(e) => setSim((s) => ({ ...s, caller_number: e.target.value }))} />
          <select className={inputCls} value={sim.scenario} data-testid="sim-scenario"
            onChange={(e) => setSim((s) => ({ ...s, scenario: e.target.value }))}>
            {SCENARIOS.map(([id, l]) => <option key={id} value={id}>{l}</option>)}
          </select>
          <Btn data-testid="sim-run" disabled={simBusy || !sim.caller_number.trim()} onClick={runSim}>
            {simBusy ? "Simulating…" : "Run simulated call"}
          </Btn>
        </div>
        {sim.scenario !== "missed" && (
          <input className={inputCls + " mt-3"} placeholder="What the caller says, e.g. “Do I need a patch test?”" value={sim.message}
            data-testid="sim-message" onChange={(e) => setSim((s) => ({ ...s, message: e.target.value }))} />
        )}
        {simResult && (
          <div className="mt-4 rounded-xl border border-neutral-200 bg-neutral-50 p-4" data-testid="sim-result">
            <div className="flex flex-wrap items-center gap-2">
              <Pill tone={OUTCOME_TONE[simResult.call.outcome] || "grey"}>{simResult.call.outcome}</Pill>
              <span className="text-[11px] text-neutral-500">{simResult.call.agent_name}</span>
              <span className="text-[11px] text-neutral-400">CRM: {simResult.lead_action === "matched_existing" ? "matched existing lead (not modified)" : "new SIMULATED test lead created"}</span>
            </div>
            <div className="mt-3 space-y-1.5" data-testid="sim-transcript">
              {simResult.call.transcript.map((line, i) => (
                <p key={i} className="text-xs leading-relaxed">
                  <span className={`font-bold ${line.speaker === "agent" ? "text-neutral-900" : line.speaker === "caller" ? "text-sky-700" : "text-neutral-400"}`}>
                    {line.speaker === "agent" ? "Receptionist" : line.speaker === "caller" ? "Caller" : "System"}:
                  </span>{" "}
                  <span className="text-neutral-600">{line.text}</span>
                </p>
              ))}
            </div>
            <p className="mt-3 border-t border-neutral-200 pt-2 text-[11px] font-medium text-amber-700" data-testid="sim-notice">{simResult.notice}</p>
          </div>
        )}
      </Card>

      {/* History */}
      <Card className="p-5" data-testid="call-history-card">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-xs font-bold uppercase tracking-wider text-neutral-500">Incoming call history</p>
          <div className="flex flex-wrap gap-2" data-testid="history-filters">
            <select className={inputCls + " w-auto"} value={filters.outcome} data-testid="filter-outcome"
              onChange={(e) => { const f = { ...filters, outcome: e.target.value }; setFilters(f); loadCalls(f); }}>
              <option value="">All outcomes</option>
              {outcomes.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
            <select className={inputCls + " w-auto"} value={filters.agent_id} data-testid="filter-agent"
              onChange={(e) => { const f = { ...filters, agent_id: e.target.value }; setFilters(f); loadCalls(f); }}>
              <option value="">All agents</option>
              {agents.map((a) => <option key={a.id} value={a.id}>{a.agent_name}</option>)}
            </select>
            <input type="date" className={inputCls + " w-auto"} value={filters.date_from} data-testid="filter-from"
              onChange={(e) => { const f = { ...filters, date_from: e.target.value }; setFilters(f); loadCalls(f); }} />
            <input type="date" className={inputCls + " w-auto"} value={filters.date_to} data-testid="filter-to"
              onChange={(e) => { const f = { ...filters, date_to: e.target.value }; setFilters(f); loadCalls(f); }} />
          </div>
        </div>
        {!calls ? null : calls.length === 0 ? (
          <Empty testid="calls-empty" icon={PhoneIncoming} title="No incoming calls yet" body="Run a simulated call above to test your receptionist. Real calls appear here once telephony is connected." />
        ) : (
          <div className="mt-4 divide-y divide-neutral-100">
            {calls.map((c) => (
              <button key={c.id} onClick={() => openDetail(c.id)} data-testid={`call-row-${c.id}`}
                className="flex w-full flex-wrap items-center gap-x-4 gap-y-1 py-3 text-left transition-colors hover:bg-neutral-50">
                <span className="w-36 text-xs text-neutral-500">{(c.started_at || "").replace("T", " ").slice(0, 16)}</span>
                <span className="flex items-center gap-1.5 text-xs font-semibold text-[#111111]"><User size={12} /> {c.caller_name}</span>
                <span className="text-[11px] text-neutral-400">{c.caller_number}</span>
                <span className="hidden items-center gap-1 text-[11px] text-neutral-500 md:flex"><Clock size={11} /> {c.duration_sec == null ? "—" : `${c.duration_sec}s`}</span>
                <Pill tone={OUTCOME_TONE[c.outcome] || "grey"}>{c.outcome}</Pill>
                {c.simulated && <Pill tone="amber">SIMULATED</Pill>}
                {c.transfer_requested && <PhoneForwarded size={13} className="text-amber-600" />}
              </button>
            ))}
          </div>
        )}
      </Card>

      {/* Recording & privacy */}
      <Card className="p-5" data-testid="recording-card">
        <div className="flex items-center gap-2">
          <ShieldCheck size={16} className="text-neutral-700" />
          <p className="text-xs font-bold uppercase tracking-wider text-neutral-500">Recording & privacy</p>
        </div>
        <p className="mt-2 text-xs text-neutral-600" data-testid="recording-status">
          <span className="font-semibold">Recording: disabled (default).</span> {status.recording.detail}
        </p>
      </Card>

      {/* Call detail modal */}
      {detailState !== "idle" && (
        <Modal title={detail ? `Call — ${detail.caller_name}` : "Call details"} onClose={closeDetail} testid="call-detail-modal">
          {detailState === "loading" && (
            <p className="py-10 text-center text-xs text-neutral-400" data-testid="call-detail-loading">Loading call details…</p>
          )}
          {detailState === "error" && (
            <div className="py-8 text-center" data-testid="call-detail-error">
              <p className="text-sm font-semibold text-red-600">Couldn't load this call's details</p>
              <p className="mt-1 text-xs text-neutral-500">It may have been removed, or the connection failed. Please close and try again.</p>
            </div>
          )}
          {detailState === "ready" && detail && (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <Pill tone={OUTCOME_TONE[detail.outcome] || "grey"}>{detail.outcome}</Pill>
              {detail.simulated && <Pill tone="amber">SIMULATED</Pill>}
              <span className="text-[11px] text-neutral-500">{(detail.started_at || "").replace("T", " ").slice(0, 16)} · {detail.agent_name}</span>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <p className="text-neutral-500">Caller: <span className="font-medium text-neutral-800">{detail.caller_number}</span></p>
              <p className="text-neutral-500">Duration: <span className="font-medium text-neutral-800">{detail.duration_sec == null ? "Not recorded (simulated)" : `${detail.duration_sec}s`}</span></p>
              <p className="text-neutral-500">Recording: <span className="font-medium text-neutral-800">{detail.recording_status}</span></p>
              <p className="text-neutral-500">Transfer: <span className="font-medium text-neutral-800">{detail.transfer_requested ? "Requested (not dialled)" : "No"}</span></p>
              {detail.requested_service && (
                <p className="text-neutral-500">Requested: <span className="font-medium text-neutral-800" data-testid="call-requested-service">{detail.requested_service}{detail.requested_time_text ? ` — ${detail.requested_time_text}` : ""}</span></p>
              )}
              {detail.lead_id && (
                <p className="text-neutral-500">CRM lead: <Link to={`/dashboard/leads?leadId=${detail.lead_id}`} className="font-medium text-neutral-800 underline" data-testid="call-lead-link">Open linked CRM lead</Link></p>
              )}
              {!detail.lead_id && detail.lead_note && (
                <p className="col-span-2 text-neutral-500" data-testid="call-lead-note">{detail.lead_note}{" "}
                  <Link to="/dashboard/leads" className="font-medium text-neutral-800 underline">Open CRM Leads</Link></p>
              )}
            </div>
            <div className="rounded-xl bg-neutral-50 p-3">
              <p className="mb-1 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-neutral-500"><FileText size={11} /> Summary</p>
              <p className="text-xs text-neutral-700" data-testid="call-summary">{detail.summary}</p>
            </div>
            {detail.appointment_id
              ? <p className="flex items-center gap-1.5 text-xs text-neutral-600"><CalendarClock size={12} /> Linked appointment: {detail.appointment_id}</p>
              : <p className="text-[11px] text-neutral-400">No appointment created (simulated calls never modify the real calendar).</p>}
            <div className="space-y-1.5 border-t border-neutral-100 pt-3" data-testid="call-transcript">
              {(detail.transcript || []).length === 0
                ? <p className="text-[11px] text-neutral-400">No transcript available for this call.</p>
                : detail.transcript.map((line, i) => (
                <p key={i} className="text-xs leading-relaxed">
                  <span className={`font-bold ${line.speaker === "agent" ? "text-neutral-900" : line.speaker === "caller" ? "text-sky-700" : "text-neutral-400"}`}>
                    {line.speaker === "agent" ? "Receptionist" : line.speaker === "caller" ? "Caller" : "System"}:
                  </span>{" "}
                  <span className="text-neutral-600">{line.text}</span>
                </p>
              ))}
            </div>
          </div>
          )}
        </Modal>
      )}
    </div>
  );
}
